import classNames from 'classnames';
import { is } from 'immutable';
import moment from 'moment';
import {
    always,
    equals,
    evolve,
    keys,
    mapObjIndexed,
    pathOr,
    pipe,
    when
} from 'ramda';
import React, { CSSProperties as css, useEffect, useMemo, useState, createContext } from 'react';
import GoogleMap from 'react-google-maps/lib/components/GoogleMap';
import { MAP } from 'react-google-maps/lib/constants';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ReactComponent as Arrow } from "../../../assets/svg/arrow_back.svg";
import EditIcon from '../../../assets/svg/edit.svg';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import { useItemNotes } from '../../../hooks/useItemNotes';
import { useCanEditNotes } from '../../../hooks/useCanEditNotes';
import { ACL, UserCan } from '../../../shared/constants';
import * as devicedb from '../../../shared/db/devices-db';
import { getHideExtraInfoValue, getPinVisibility } from "../../../shared/db/extra-info-db";
import {
    DeviceInfoHidden,
    DeviceInfoSectionOrderItem, DeviceInfoSectionVisibilityMap, getDeviceInfoHiddenSettings,
    getDeviceInfoSectionFullOrder,
} from '../../../shared/db/general-db';
import * as tagSelectors from '../../../shared/db/tags-labels-selectors';
import { clientDb } from '../../../shared/firebase';
import { idValArr } from '../../../shared/helpers';
import {
    ExtraInfoFields,
    ItemType,
    MenuPinType,
    iDeviceDetails,
    iDevicePing,
    iFullStoreState,
    iList,
    iNote,
    iPerson,
} from '../../../shared/interfaces';
import { useFirebase } from '../../../states/firebase-state';
import { useRedux } from '../../../states/redux-state';
import { DevicesDetailsContainer } from '../../../stores/reducers/devicesData';
import { Actions as MapActions, defaultBounds } from '../../../stores/reducers/gmap-reducers';
import { getExtraInfoKeyByName } from "../../../utils";
import { compareByNameAscending } from '../../../utils/sorting';
import DashboardBlock, { SearchInputDesktop } from '../../DashboardBlock';
import Dialog, { DialogConfigSetter } from '../../Dialog';
import { ExtraInfo } from '../../elements/ExtraInfo/extra-info';
import { Row } from '../../elements/flex';
import { AlertToggles } from '../../general/alert-toggle';
import Notepad from '../../general/Notepad';
import StandardTile from '../../general/StandardTile';
import ModalHeader from '../../menus/modal/modal-header';
import ModalImage from '../../ModalImage';
import { DeviceAssignedDriver } from "./AssignedDriver";
import styles from './device-details.module.scss';
import { DeviceButtons } from "./DeviceButtons";
import { CheckEngineOverlay } from './DeviceDetails/CheckEngineOverlay';
import { EngineCodeTable } from './DeviceDetails/EngineCodeTable';
import DeviceEngineHistory from "./DeviceEngineHistory";
import DeviceHeader from './DeviceHeader';
import { DeviceTags } from "./DeviceTags";
import { useTbs } from './hooks/useTbs';
import { DeviceVisibleToPeople } from "./VisibleToPeople";
// import {toast} from "react-toastify";
import { Tooltip } from '@mui/material';
import { Fa } from '../../elements/fa';
import { faHome } from '@fortawesome/fontawesome-free-solid';

export const DeviceDetailsContext = createContext<{
    deviceImage: any;
    setDeviceImage: React.Dispatch<React.SetStateAction<any>>;
    deviceId: string | undefined;
}>({
    deviceImage: {},
    setDeviceImage: () => { },
    deviceId: undefined,
});

interface LocationState {
    sortedDevices: Array<string>;
}

export default ({ match: { params: { id: deviceId } } }) => {
    const dispatch = useDispatch();
    const config = useRedux((s) => s.general.config);
    const auth = useRedux((s) => s.auth);
    const { user: authUser, isDriver } = auth;

    const isSearchVis = useSelector<iFullStoreState, boolean>((s) => s.general.showSearch);

    const devices = useSelector<iFullStoreState, DevicesDetailsContainer>(
        (s) => s.devicesData.devicesDetails,
        (l, r) => is(l, r)
    );

    const devicesArray = devices.valueSeq().toArray();
    const devicesIdsArraySorted = devicesArray
        .sort(compareByNameAscending)
        .map(({ id }) => id);

    const device = useSelector<iFullStoreState, iDeviceDetails | undefined>(
        (state) => state.devicesData.devicesDetails.get(deviceId),
        (l, r) => equals(l, r),
    );
    const currentLabels = useRedux((s) => tagSelectors.getItemLabelNameValMap(s, { type: ItemType.device, itemId: deviceId }));

    const countPerson = useSelector<iFullStoreState, number>((s) => Object.keys(s.general.people).length);

    const assignedPersonId = device?.assignedTo?.personId;
    const assignedPerson = useRedux<iPerson | false>(pathOr(false, ['general', 'people', assignedPersonId]));
    const isDriverDevice = assignedPersonId === authUser.uid && isDriver;
    const deviceLastPing = useSelector<iFullStoreState, iDevicePing | undefined | null>(
        (state) => state.devicesData.devicesLastPing.get(device.id),
        (l, r) => typeof l === typeof r && l?.pointId === r?.pointId,
    );

    const people = useRedux((s) => s.general.people);

    const allTags = useRedux((s) => s.general.tags);
    const itemTags = React.useMemo(() => tagSelectors.itemTags(allTags, ItemType.device, deviceId), [allTags, deviceId]);

    const [CEOverlay, setCEOverlay] = React.useState(false);
    const deviceNotes = useItemNotes('device', deviceId);
    const readOnly = useIsReadOnly(deviceId, ItemType.device)
    const dialogRef = React.useRef<DialogConfigSetter>();
    const [menuPinSettings, setMenuPinSettings] = useState<{ [key: string]: boolean }>({});
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [deviceImage, setDeviceImage] = useState<any>({});

    const [filteredLabels, setFilteredLabels] = useState([]);
    const [showPin, setShowPin] = useState<boolean>(true);
    const setupDialog = (callBack: () => DialogConfigSetter) => dialogRef.current = callBack();

    const history = useHistory();
    const location = useLocation();
    const params = useParams();

    const menuTitlesForDevicePage = {
        tags: process.env.REACT_APP_TAGS_DEVICE_MENU_ITEM_NAME || 'Tags',
        extraInfo: null,
        assignedDriver: process.env.REACT_APP_ASSIGNED_DRIVER_DEVICE_MENU_ITEM_NAME || 'Assigned Driver',
        alerts: process.env.REACT_APP_ALERTS_DEVICE_MENU_ITEM_NAME || 'Alerts',
        engineCodeHistory: process.env.REACT_APP_ENGINE_CODE_HISTORY_DEVICE_MENU_ITEM_NAME || 'Engine Code History',
        visibleTo: process.env.REACT_APP_VISIBLE_TO_DEVICE_MENU_ITEM_NAME || 'Visible to...',
        notes: process.env.REACT_APP_NOTES_MENU_ITEM_NAME || "Notes",
    }

    // TODO: CONDITIONAL HOOKS
    // there is hooks below that are useing devie as a parameter
    // need to remove that condition and remain the same behaviour
    if (!device) return null;

    const editDevice = () => {
        if (readOnly) {
            const dialog = dialogRef.current;

            dialog?.({
                type: 'NOTIFICATION',
                title: 'Reed only device!',
                body: 'This device is read only',
            });
        } else {
            history.push(`/device/${deviceId}/edit`);
        }

    };

    const updateDeviceTitle = async (newName: string) => {
        if (newName.trim() !== device.name && deviceId) {
            try {
                await devicedb.updateDeviceDetails(authUser)(
                    deviceId,
                    {
                        name: newName.trim(),
                    }
                );
                // toast.success('Device name updated');
            } catch (error) {
                console.error('Failed to update device name:', error);
                // toast.error('Failed to update device name');
            }
        }
    };

    const overlay = when(Boolean, () => <CheckEngineOverlay
        deviceId={device.id}
        dismiss={pipe(always(device.id), devicedb.dismissCheckEngine, () => setCEOverlay(false))}
        close={() => setCEOverlay(false)}
    />, CEOverlay);

    const tags = idValArr(itemTags || {});
    const isReadOnly = useIsReadOnly(deviceId, ItemType.device);
    const { disabled: notesDisabled } = useCanEditNotes(deviceId, ItemType.device);

    const userCanDo = useSelector<iFullStoreState, Array<string>>(
        (s) => s.auth.user?.acl?.can
    );
    const showTappedPeople = ACL.check(UserCan.SEE_TAPPED_PEOPLE, userCanDo, true);
    const showTags = ACL.check(UserCan.SEE_TAGS, userCanDo, true);
    const mapRef = useSelector<iFullStoreState, React.RefObject<GoogleMap> | null>(state => state.gmap.mapRef());

    const lastLocation = useRedux((s) => s.devicesData.devicesLastPing.get(deviceId))?.coordinates?.location;
    const currentBounds = useRedux((s) => s.gmap.bounds);

    const closeImageHandler = (e) => {
        e.stopPropagation();
        setImageSrc(null);
    }

    const getFilteredLabels = async () => {
        const combinedValues: Record<string, string> = {};
        const namesToRemove = [ExtraInfoFields.YEAR, ExtraInfoFields.MAKE, ExtraInfoFields.MODEL];

        const labelEntries = await Promise.all(
            currentLabels.map(async (label) => {
                const [name, value] = label;

                const key = await getExtraInfoKeyByName(name);
                const hideValue = await getHideExtraInfoValue(key);

                if (namesToRemove.includes(name)) {
                    let textValue = '';

                    if (typeof value === 'string') {
                        textValue = value;
                    } else if (value && typeof value === 'object' && typeof value.text === 'string') {
                        textValue = value.text;
                    }

                    combinedValues[name] = textValue;

                    return null;
                }

                return hideValue !== true ? label : null;
            })
        );

        const newLabels = labelEntries.filter((label): label is [string, any] => label !== null);

        const yearValue = combinedValues[ExtraInfoFields.YEAR] || '';
        const makeValue = combinedValues[ExtraInfoFields.MAKE] || '';
        const modelValue = combinedValues[ExtraInfoFields.MODEL] || '';
        const combinedValue = `${yearValue} ${makeValue} ${modelValue}`.trim();

        if (combinedValue !== '') {
            newLabels.unshift([ExtraInfoFields.MAKE, combinedValue]);
        }

        setFilteredLabels(newLabels);
    };


    useEffect(() => {
        if (lastLocation?.lat < currentBounds.latSouth || lastLocation?.lat > currentBounds.latNorth ||
            lastLocation?.lng < currentBounds.lngWest || lastLocation?.lng > currentBounds.lngEast) {
            dispatch(MapActions.SET_BOUNDS(defaultBounds));
        }

    }, [lastLocation]);

    useEffect(() => {
        mapRef && mapRef.current && mapRef.current.context[MAP].setZoom(10);
        lastLocation?.lat && lastLocation?.lng && mapRef && mapRef.current && mapRef.current.context[MAP].setCenter({ lat: lastLocation.lat, lng: lastLocation.lng });

        const getMenuPins = async () => {
            const fetchedMenuPinSettings = (await clientDb().child(`/people/people-details/${authUser.uid}/menuPin`).once('value')).val();
            if (fetchedMenuPinSettings) setMenuPinSettings(fetchedMenuPinSettings);
        }

        const getShowPin = async () => {
            const value = await getPinVisibility();
            setShowPin(value !== false);
        }

        getMenuPins();
        getShowPin();
        getFilteredLabels();
    }, [deviceId])

    const [deviceInfoSectionOrder, setDeviceInfoSectionOrder] = useState<DeviceInfoSectionOrderItem[]>([]);
    useEffect(() => {
        getDeviceInfoSectionFullOrder().then((order) => {
            setDeviceInfoSectionOrder(order);
        });
    }, []);

    const itemsCount = deviceInfoSectionOrder.length;
    const cssOrderValue = {};
    deviceInfoSectionOrder.forEach((curItem, index) => {
        // first item should have the smallest value
        // ans the last one should have '-1' value
        // so css order should calc as index - allCount
        cssOrderValue[curItem] = index - itemsCount;
    });

    const handleImageClick = (src) => {
        setImageSrc(src);
    }

    const centerOnDevice = () => {
        dispatch(MapActions.RECENTER_MAP_TO_DEVICE(device.id));
    }

    const { isTbs } = useTbs(deviceId);

    const iconUrl = useMemo(() => (
        device.icon?.type === 2 ? device.icon.url : ''
    ), [device.id, device.icon]);

    const DeviceNavigationButtons = () => {
        const { id: currentDeviceId } = params as { id: string };
        const sortedDevices = (location?.state as LocationState)?.sortedDevices ?? devicesIdsArraySorted;
        const currentDeviceIdx = sortedDevices.indexOf(currentDeviceId);

        const goToDevice = (index: number) => {
            if (index >= 0 && index < sortedDevices.length) {
                const nextDeviceId = sortedDevices[index];
                history.push({
                    pathname: `/device/${nextDeviceId}`,
                    state: { sortedDevices }
                });
            }
        };

        const isFirst = currentDeviceIdx <= 0;
        const isLast = currentDeviceIdx >= sortedDevices.length - 1;

        return (
            <>
                <Tooltip title="Prev" placement="bottom">
                    <button
                        onClick={() => goToDevice(currentDeviceIdx - 1)}
                        className={styles.deviceNavButton}
                        disabled={isFirst}
                    >
                        <Arrow />
                    </button>
                </Tooltip>
                <Tooltip title="Home" placement="bottom">
                    <button
                        onClick={() => {
                            history.push({
                                pathname: `/device`,
                            });
                        }}
                        className={styles.deviceHomeButton}
                    >
                        <Fa icon={faHome} />
                    </button>
                </Tooltip>
                <Tooltip title="Next" placement="bottom" >
                    <button
                        onClick={() => goToDevice(currentDeviceIdx + 1)}
                        className={styles.deviceNavButton}
                        disabled={isLast}
                    >
                        <Arrow />
                    </button>
                </Tooltip>
            </>
        );
    };

    const [hiddenSections, setHiddenSections] = useState<DeviceInfoSectionVisibilityMap>({});

    useEffect(() => {
        const loadHiddenSettings = async () => {
            const result = await getDeviceInfoHiddenSettings();
            setHiddenSections(result);
        };

        loadHiddenSettings().then(() => {});
    }, []);


    const fixedHeader = (
        <ModalHeader
            title={''}
            role={isDriver}
            isEditableTitle
            paginateComponent={<DeviceNavigationButtons />}
            className={styles.deviceHeader}
        />
    );

    return <DashboardBlock overlay={overlay} className={classNames({ [styles.tbsBackground]: isTbs && !isSearchVis })} header={fixedHeader}>
        {/* {!deviceLastPing ? null : */}
            <Row wrap>
                <DeviceDetailsContext.Provider value={{ deviceImage, setDeviceImage, deviceId }}>
                    <DeviceHeader
                        name={device.name}
                        address={device?.isArchived ? '' : (deviceLastPing?.address || null)}
                        clickHandler={centerOnDevice}
                        iconUrl={deviceImage.icon?.url || iconUrl}
                        editDeviceInfo={{
                            editDeviceHandler: editDevice,
                            isShowEditButton: false,
                            onNameUpdate: updateDeviceTitle
                        }}
                    />
                </DeviceDetailsContext.Provider>
            </Row>
        {/* } */}
        {/*<div style={{ display: 'flex', flexWrap: 'wrap', minHeight: 'min-content' }}>*/}
        {/*    {pipe(*/}
        {/*        pathOr([], ['externalData', 'keyVals']),*/}
        {/*        mapObjIndexed((v, k) => <LocalMinTile key={k} title={k}>{v}</LocalMinTile>),*/}
        {/*        values*/}
        {/*    )(device)}*/}
        {/*</div>*/}

        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'min-content', marginTop: 8 }}>
            {showTags && hiddenSections[DeviceInfoHidden.LAST_PING] === true || !(DeviceInfoHidden.LAST_PING in hiddenSections) ?
                <DeviceLastPingDetails
                    device={device}
                    setCEOverlay={setCEOverlay}
                    isDriver={isDriver}
                    cssOrder={cssOrderValue[DeviceInfoSectionOrderItem.LAST_PING]}
                    deviceLastPing={deviceLastPing}
                /> : null
            }
            {/*<div style={{borderTop: '2px solid' + C.primaryText, marginTop: 4, marginBottom: 6}}/>*/}

            {showTags && hiddenSections[DeviceInfoHidden.TAGS] === true || !(DeviceInfoHidden.TAGS in hiddenSections) ? <DeviceTags
                     deviceId={deviceId}
                     tags={tags}
                     isPinned={menuPinSettings[MenuPinType.TAGS] || false}
                     isDriver={isDriver}
                     readOnly={readOnly}
                     title={menuTitlesForDevicePage.tags}
                     cssOrder={cssOrderValue[DeviceInfoSectionOrderItem.TAGS]}
                     showPin={showPin}
                /> : null
            }
            {hiddenSections[DeviceInfoHidden.EXTRA_INFO] === true || !(DeviceInfoHidden.EXTRA_INFO in hiddenSections) ?
                <ExtraInfo
                    editUrl={`/${ItemType.device}/${deviceId}/add/extra-info`}
                    labels={filteredLabels}
                    role={isDriver}
                    readOnly={isReadOnly}
                    itemId={device.id}
                    itemType={ItemType.device}
                    itemName={device.name}
                    isPinned={true}
                    property={MenuPinType.EXTRA_INFO}
                    imageClick={handleImageClick}
                    title={menuTitlesForDevicePage.extraInfo}
                    cssOrder={cssOrderValue[DeviceInfoSectionOrderItem.EXTRA_INFO]}
                    showPin={false}
                    formatNumbersByKind={true}
                /> : null
            }
            {(config?.strictAccess && countPerson > 1 && authUser.acl.can.includes(UserCan.DO_ANYTHING)) &&
                <React.Fragment>
                    {hiddenSections[DeviceInfoHidden.VISIBLE_TO] === true || !(DeviceInfoHidden.VISIBLE_TO in hiddenSections) ?
                        <DeviceVisibleToPeople
                            deviceId={deviceId}
                            deviceTags={keys(itemTags)}
                            people={people}
                            isPinned={menuPinSettings[MenuPinType.VISIBLE_TO_PEOPLE] || false}
                            title={menuTitlesForDevicePage.visibleTo}
                            cssOrder={cssOrderValue[DeviceInfoSectionOrderItem.VISIBLE_TO]}
                            showPin={showPin}
                        /> : null
                    }
                </React.Fragment>
            }
            {(countPerson > 1 && showTappedPeople) && hiddenSections[DeviceInfoHidden.ASSIGNED_DRIVER] === true || !(DeviceInfoHidden.ASSIGNED_DRIVER in hiddenSections) ? <DeviceAssignedDriver
                deviceId={deviceId}
                isPinned={menuPinSettings[MenuPinType.ASSIGNED_DRIVER] || false}
                isDriver={isDriver}
                isDriverDevice={isDriverDevice}
                assignedPerson={assignedPerson}
                device={device}
                assignedPersonId={assignedPersonId}
                isReadOnly={isReadOnly}
                authUser={authUser}
                title={menuTitlesForDevicePage.assignedDriver}
                cssOrder={cssOrderValue[DeviceInfoSectionOrderItem.ASSIGNED_DRIVER]}
                showPin={showPin}
            /> : null}

            {
                !isDriver && (
                    <>
                        {hiddenSections[DeviceInfoHidden.ALERT] === true || !(DeviceInfoHidden.ALERT in hiddenSections) ?
                            <AlertToggles
                                itemIdProps={deviceId}
                                itemTypeProps={ItemType.device}
                                style={{ width: '100%' }}
                                itemId={device.id}
                                itemType={ItemType.device}
                                values={device.eventValues || {}}
                                eventKeys={device.deviceEvents}
                                deviceId={deviceId}
                                property={MenuPinType.ALERTS}
                                isPinned={menuPinSettings[MenuPinType.ALERTS] || false}
                                title={menuTitlesForDevicePage.alerts}
                                cssOrder={cssOrderValue[DeviceInfoSectionOrderItem.ALERT]}
                                showPin={showPin}
                            /> : null
                        }
                        {hiddenSections[DeviceInfoHidden.ENGINE_HISTORY] === true || !(DeviceInfoHidden.ENGINE_HISTORY in hiddenSections) ?
                            <DeviceEngineHistory
                                body={<EngineCodeTable deviceId={deviceId} />}
                                alwaysOpen={false}
                                title={menuTitlesForDevicePage.engineCodeHistory}
                                cssOrder={cssOrderValue[DeviceInfoSectionOrderItem.ENGINE_HISTORY]}
                                property={MenuPinType.ENGINE_HISTORY}
                                isPinned={menuPinSettings[MenuPinType.ENGINE_HISTORY] || false}
                                showPin={showPin}
                            /> : null
                        }
                        {hiddenSections[DeviceInfoHidden.NOTES] === true || !(DeviceInfoHidden.NOTES in hiddenSections) ?
                            <div className="col3" style={{ width: '100%', minWidth: 200, flex: 1, display: 'flex', flexDirection: 'column', order: cssOrderValue[DeviceInfoSectionOrderItem.NOTES] }}>
                                <Notepad
                                    disabled={notesDisabled}
                                    notes={deviceNotes}
                                    deviceOrFence="device"
                                    deviceOrFenceId={deviceId}
                                    title={menuTitlesForDevicePage.notes}
                                    maxVisibleRows={10}
                                />
                            </div> : null
                        }
                    </>
                )
            }
        </div>
        {imageSrc && (
            <ModalImage
                closeImageHandler={closeImageHandler}
                imageSrc={imageSrc}
            />
        )}
        <Dialog setupConfig={setupDialog} />
    </DashboardBlock>
};

const DeviceLastPingDetails = ({ device, setCEOverlay, isDriver, cssOrder = null, deviceLastPing }: { device: iDeviceDetails; setCEOverlay: (p) => void; isDriver: boolean, cssOrder?: number | null, deviceLastPing?: iDevicePing }) => {
    const dialogRef = React.useRef<DialogConfigSetter>();
    const setupDialog = (callBack: () => DialogConfigSetter) => dialogRef.current = callBack();
    const availableDeviceLocation = !!deviceLastPing;
    const isInventoryOnlyDevice = device?.isInventoryOnlyDevice;



    return <section style={{ marginBottom: '8px', order: cssOrder }}>
        <DeviceButtons device={device} isDriver={isDriver} onCheckEngineClick={setCEOverlay} />

        <Dialog setupConfig={setupDialog} />
    </section>;
};

const localTileStyle: css = {
    margin: 3,
    marginBottom: 6,
};
export const LocalTile = ({
    style = {} as React.CSSProperties,
    title = '',
    children,
    action,
    collapsible = false,
    initialCollapsed = false,
    loading = false,
    deviceId = undefined,
    property = undefined,
    isPinned = false }) => (
    <StandardTile
        style={{ ...style, ...localTileStyle }}
        title={title}
        action={action}
        collapsible={collapsible}
        initialCollapse={initialCollapsed}
        loading={loading}
        deviceId={deviceId}
        property={property}
        isPinned={isPinned}
    >
        {children}
    </StandardTile>
);
